// RollPrompt — when the DM calls for a check from a character YOU control, a dramatic
// dice button appears in the narrative flow. You press it to roll your own dice; the
// DC is NEVER shown (only the DM knows it). Pressing tumbles a d20 in the card; when the
// resolved roll is known it snaps to the natural face, the total counts up large, and a
// nat 20 bursts (.roll-crit) / nat 1 cracks (.roll-fumble) before the beat collapses into
// the stream. This is the "feel the dice" moment.
import { useEffect, useRef, useState } from "react";
import type { PendingRoll, WsClientMessage } from "../lib/types";

// Minimal resolved-roll shape for the in-card reveal. Optional: when the table wires the
// resolved player_roll back to its prompt, the die snaps to the real face + total; absent,
// the prompt tumbles then hands the reveal to the stream beat.
export interface RollPromptResult {
  natural: number | null;
  total: number;
  bonus?: number;
}

export function RollPrompt({
  roll, onRoll, result,
}: {
  roll: PendingRoll;
  onRoll: (msg: WsClientMessage) => boolean;
  result?: RollPromptResult | null;
}) {
  const [rolling, setRolling] = useState(false);
  const [face, setFace] = useState(20);
  const [shownTotal, setShownTotal] = useState<number | null>(null);
  const timers = useRef<number[]>([]);
  const tumble = useRef<number | null>(null);
  const count = useRef<number | null>(null);

  const clearAll = () => {
    timers.current.forEach(window.clearTimeout);
    timers.current = [];
    if (tumble.current) { window.clearInterval(tumble.current); tumble.current = null; }
    if (count.current) { window.clearInterval(count.current); count.current = null; }
  };
  // Clear pending timers on unmount (the prompt disappears once the roll resolves).
  useEffect(() => () => clearAll(), []);

  // When the resolved roll arrives, snap the die to its natural face and count the total up.
  useEffect(() => {
    if (!result) return;
    if (tumble.current) { window.clearInterval(tumble.current); tumble.current = null; }
    if (result.natural != null) setFace(result.natural);
    let i = 0;
    const steps = 16;
    if (count.current) window.clearInterval(count.current);
    count.current = window.setInterval(() => {
      i += 1;
      const t = i / steps;
      const eased = 1 - Math.pow(1 - t, 3);
      setShownTotal(Math.round(result.total * eased));
      if (i >= steps) { if (count.current) window.clearInterval(count.current); count.current = null; setShownTotal(result.total); }
    }, 26);
  }, [result]);

  function doRoll() {
    if (rolling) return;
    setRolling(true);
    // Tumble the die through faces while suspense builds, then send. The server resolves
    // against the hidden DC and logs the authoritative roll.
    if (tumble.current) window.clearInterval(tumble.current);
    tumble.current = window.setInterval(() => setFace(1 + Math.floor(Math.random() * 20)), 70);
    timers.current.push(window.setTimeout(() => {
      // send() returns false when the socket is closed/reconnecting — nothing was
      // transmitted, so drop back out of 'Rolling…' rather than wedging forever.
      const ok = onRoll({ action: "player_roll", roll_id: roll.id });
      if (!ok) { if (tumble.current) { window.clearInterval(tumble.current); tumble.current = null; } setRolling(false); return; }
      // Even on a successful send, if the server never answers (dropped frame, reconnect
      // mid-flight) the prompt would stay stuck. Re-enable after a grace window so the
      // player can retry; a resolved roll unmounts us first.
      timers.current.push(window.setTimeout(() => setRolling(false), 6000));
    }, 700));
  }

  const advLabel = roll.advantage === "advantage" ? "Advantage"
    : roll.advantage === "disadvantage" ? "Disadvantage" : "";
  const what = roll.skill
    ? roll.skill.replace(/(^|\s)\w/g, (c) => c.toUpperCase())
    : roll.ability.toUpperCase();

  const crit = !!result && result.natural === 20;
  const fumble = !!result && result.natural === 1;
  const revealing = rolling || !!result;

  return (
    <div className={`roll-prompt${crit ? " roll-crit" : ""}${fumble ? " roll-fumble" : ""}`}>
      <div className="roll-prompt__body">
        <div className="roll-prompt__head">
          <span className="roll-prompt__die">🎲</span>
          <span className="roll-prompt__call">
            The DM calls for a roll — <b>{roll.character_name}</b>
          </span>
        </div>
        <div className="roll-prompt__reason">{roll.reason}</div>
        <div className="roll-prompt__meta">
          {roll.kind === "save" ? `${what} save` : `${what} check`}
          {advLabel && <span className="roll-prompt__adv"> · {advLabel}</span>}
        </div>

        {revealing ? (
          <div className="roll-reveal" style={{ textAlign: "center", padding: "6px 0" }}>
            <div className={`roll-reveal__die${rolling && !result ? " is-tumbling" : ""}`}
              aria-hidden
              style={{
                width: 52, height: 52, margin: "0 auto 4px", display: "flex", alignItems: "center",
                justifyContent: "center", borderRadius: 9, fontWeight: 800, fontSize: 24,
                border: "2px solid currentColor", opacity: 0.92,
                transition: result ? "transform 140ms ease-out" : "none",
                transform: result ? "scale(1.08)" : "rotate(-5deg)",
              }}>
              {face}
            </div>
            {/* total only — NO DC, NO pass/fail: the difficulty stays the DM's secret */}
            {result && (
              <div className="dr-total" style={{ fontSize: 26, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                {shownTotal ?? result.total}
              </div>
            )}
            {crit && <div className="dr-label">Natural 20!</div>}
            {fumble && <div className="dr-label">Natural 1…</div>}
          </div>
        ) : (
          <button className="roll-prompt__btn" onClick={doRoll} disabled={rolling}>
            <span className="roll-prompt__btn-die">🎲</span>
            Roll the dice
          </button>
        )}

        {!result && (
          <div className="roll-prompt__hint">You don't know the difficulty — fortune favors the bold.</div>
        )}
      </div>
    </div>
  );
}
